(() => {
  const DEFAULT_ITEMS_PER_PAGE = 10;

  class AssemblyCalculator {
    constructor(el) {
      this.el = el;
      this.tool = el.dataset.tool; // 'wall' or 'floor-ceiling'
      this.data = this._parseData();
      this.filtered = [...this.data];
      this.page = 1;
      const perPageEl = el.querySelector('[data-per-page-select]');
      this.itemsPerPage = perPageEl ? parseInt(perPageEl.value, 10) || DEFAULT_ITEMS_PER_PAGE : DEFAULT_ITEMS_PER_PAGE;
      this.sort = { key: null, dir: 'asc' };
      this.filterState = {};
      this.quizAnswers = {};
      this.currentStep = 0;

      this._bindQuiz();
      this._bindTable();
      this._bindDrawer();
      this._bindLightbox();
      this._bindDetailsModal();
      if (this.el.hasAttribute('data-browse-mode')) {
        this._showTable(false);
        this.el.querySelectorAll('[data-action="restart"]').forEach(btn => {
          btn.querySelector('.btn__text').textContent = 'Clear Filters';
        });
      }
    }

    // ── Data ──────────────────────────────────────────────────────────────────

    _parseData() {
      const id = this.tool === 'wall' ? 'wall-assembly-data' : 'floor-ceiling-assembly-data';
      const el = document.getElementById(id);
      if (!el) return [];
      try {
        const data = JSON.parse(el.textContent);
        // Some metaobject list fields serialize as JSON-encoded strings (e.g. "[\"RC-1 Boost\"]")
        // rather than real arrays. Normalize so filter comparisons work either way.
        const listFields = ['filter_material', 'filter_structure', 'filter_finished_flooring', 'filter_iic_rating', 'filter_stc_rating'];
        data.forEach(item => {
          listFields.forEach(f => {
            const v = item[f];
            if (typeof v === 'string' && v.length > 1 && v[0] === '[' && v[v.length - 1] === ']') {
              try {
                const parsed = JSON.parse(v);
                if (Array.isArray(parsed)) item[f] = parsed;
              } catch {}
            }
          });
        });
        return data;
      } catch {
        return [];
      }
    }

    // ── Quiz ──────────────────────────────────────────────────────────────────

    _bindQuiz() {
      this.el.addEventListener('click', e => {
        const action = e.target.closest('[data-action]')?.dataset.action;
        if (!action) return;

        if (action === 'start') this._showStep(1);
        if (action === 'back') this._stepBack();
        if (action === 'next') this._stepNext();
        if (action === 'restart') this._restart();
        if (action === 'browse') this._showTable(false);
      });

      // Radio change — update state + visual selection + enable the Next button.
      // No auto-advance here so keyboard users (Tab + Arrow keys) can navigate
      // between options without being yanked to the next step. They confirm via
      // the Next button (WCAG 3.2.2: change of context shouldn't happen on input).
      this.el.addEventListener('change', e => {
        const input = e.target.closest('input[type="radio"]');
        if (!input) return;
        const step = input.closest('[data-step]')?.dataset.step;
        if (!step) return;
        this.quizAnswers[step] = input.value;

        // Mark selected option visually
        input.closest('ul')?.querySelectorAll('.calculator-option').forEach(o => o.classList.remove('calculator-option--selected'));
        input.closest('.calculator-option')?.classList.add('calculator-option--selected');

        // Enable Next button
        const nextBtn = input.closest('[data-step]')?.querySelector('[data-action="next"]');
        if (nextBtn) nextBtn.disabled = false;
      });

      // Pointer-only auto-advance: tapping/clicking an option moves the user to the
      // next step after a brief beat. Keyboard activation (Space/Enter) fires a click
      // with detail=0 and is excluded — keyboard users press Next instead, which is
      // required for screen reader and assistive tech flows.
      this.el.addEventListener('click', e => {
        if (e.detail === 0) return;
        const label = e.target.closest('.calculator-option, .calculator-option-row');
        if (!label) return;
        const input = label.querySelector('input[type="radio"]');
        if (!input || !input.checked) return;
        clearTimeout(this._autoAdvanceTimer);
        this._autoAdvanceTimer = setTimeout(() => this._stepNext(), 220);
      });
    }

    _showStep(n) {
      this.el.querySelectorAll('[data-step]').forEach(s => s.classList.add('hidden'));
      const step = this.el.querySelector(`[data-step="${n}"]`);
      if (step) step.classList.remove('hidden');
      this.currentStep = n;

      // Filter dynamic options. Material step (2) applies to any tool that uses
      // data-for-structures; flooring step (3) only floor-ceiling.
      if (n === 2) this._filterMaterialOptions();
      if (this.tool === 'floor-ceiling' && n === 3) this._filterFlooringOptions();

      // Show/hide restart wrap
      const restartWrap = this.el.querySelector('[data-restart-wrap]');
      if (restartWrap) restartWrap.classList.toggle('hidden', n === 0);
    }

    _filterMaterialOptions() {
      const structure = this.quizAnswers['1'];
      if (!structure) return;
      this.el.querySelectorAll('[data-step="2"] li[data-for-structures]').forEach(li => {
        const matches = li.dataset.forStructures.includes(structure);
        li.classList.toggle('hidden', !matches);
      });
      // Reset any previously selected option that is now hidden
      this.el.querySelectorAll('[data-step="2"] li[data-for-structures].hidden input').forEach(input => {
        input.checked = false;
        input.closest('.calculator-option')?.classList.remove('calculator-option--selected');
      });
      // Disable next button since selection may have been reset
      const nextBtn = this.el.querySelector('[data-step="2"] [data-action="next"]');
      const anyVisible = [...this.el.querySelectorAll('[data-step="2"] li[data-for-structures]:not(.hidden) input')].some(i => i.checked);
      if (nextBtn) nextBtn.disabled = !anyVisible;
    }

    _filterFlooringOptions() {
      const structure = this.quizAnswers['1'];
      const material = this.quizAnswers['2'];
      if (!structure || !material) return;
      this.el.querySelectorAll('[data-step="3"] li[data-for-combos]').forEach(li => {
        const raw = li.dataset.forCombos;
        const matches = raw.includes(structure) && raw.includes(material);
        li.classList.toggle('hidden', !matches);
      });
      // Reset hidden selections
      this.el.querySelectorAll('[data-step="3"] li[data-for-combos].hidden input').forEach(input => {
        input.checked = false;
        input.closest('.calculator-option')?.classList.remove('calculator-option--selected');
      });
      const nextBtn = this.el.querySelector('[data-step="3"] [data-action="next"]');
      const anyVisible = [...this.el.querySelectorAll('[data-step="3"] li[data-for-combos]:not(.hidden) input')].some(i => i.checked);
      if (nextBtn) nextBtn.disabled = !anyVisible;
    }

    _stepBack() {
      if (this.currentStep > 1) {
        this._showStep(this.currentStep - 1);
      } else {
        this._showStep(0);
        const restartWrap = this.el.querySelector('[data-restart-wrap]');
        if (restartWrap) restartWrap.classList.add('hidden');
      }
    }

    _stepNext() {
      const totalSteps = this.tool === 'wall' ? 2 : 3;
      const currentLi = this.el.querySelector(`[data-step="${this.currentStep}"] input:checked`)?.closest('li');

      // Any tool: redirect to /pages/contact if the selected option opts in.
      if (currentLi?.hasAttribute('data-redirect-contact')) {
        window.location.href = '/pages/contact';
        return;
      }

      // Any tool: a step 1 option with data-skip-next jumps straight to results.
      // (Used by wall Concrete which has no applicable Step 2 options.)
      if (this.currentStep === 1 && currentLi?.hasAttribute('data-skip-next')) {
        this._applyQuizFilters();
        this._showTable(true);
        return;
      }

      // Floor-ceiling: skip the flooring step if Step 2's data-skip-flooring-structures
      // includes the current structure (e.g. Concrete + Isolation Hangers, OWT + Resilient Channel).
      if (this.tool === 'floor-ceiling' && this.currentStep === 2) {
        let skipStructures = [];
        try {
          skipStructures = JSON.parse(currentLi?.dataset.skipFlooringStructures || '[]');
        } catch {}
        if (skipStructures.includes(this.quizAnswers['1'])) {
          this._applyQuizFilters();
          this._showTable(true);
          return;
        }
      }

      if (this.currentStep < totalSteps) {
        this._showStep(this.currentStep + 1);
      } else {
        this._applyQuizFilters();
        this._showTable(true);
      }
    }

    _applyQuizFilters() {
      this.filterState = {};

      // Unified flow for both wall + floor-ceiling. Each Step 1 <li> carries
      // data-structure-filter (an array of the underlying values to apply), and
      // each Step 2 <li> carries data-material-filter-by-structure (an object
      // keyed by Step 1 radio value). Falls back to the raw radio value if a
      // data attribute is missing, which keeps any older option markup working.
      const structureVal = this.quizAnswers['1'];
      const materialVal = this.quizAnswers['2'];
      const flooringVal = this.quizAnswers['3'];

      const s1Li = this.el.querySelector('[data-step="1"] input:checked')?.closest('li');
      const s2Li = this.el.querySelector('[data-step="2"] input:checked')?.closest('li');

      // Structure filter
      let structureFilter = [];
      if (s1Li?.dataset.structureFilter) {
        try { structureFilter = JSON.parse(s1Li.dataset.structureFilter); } catch {}
      }
      if (!structureFilter.length && structureVal && structureVal !== 'Other') {
        structureFilter = [structureVal];
      }
      if (structureFilter.length) this.filterState.filter_structure = structureFilter;

      // Material filter — keyed by Step 1 friendly value
      let materialFilter = [];
      if (s2Li?.dataset.materialFilterByStructure) {
        try {
          const map = JSON.parse(s2Li.dataset.materialFilterByStructure);
          materialFilter = map[structureVal] || [];
        } catch {}
      } else if (materialVal && materialVal !== 'Other' && materialVal !== 'No Preference') {
        materialFilter = [materialVal];
      }
      if (materialFilter.length) this.filterState.filter_material = materialFilter;

      // Flooring filter — radio value IS the filter value (floor-ceiling only)
      if (flooringVal && flooringVal !== 'Other') {
        this.filterState.filter_finished_flooring = [flooringVal];
      }
    }

    _restart() {
      this.quizAnswers = {};
      this.filterState = {};
      this.page = 1;
      this.sort = { key: null, dir: 'asc' };

      // Clear radio selections
      this.el.querySelectorAll('input[type="radio"]').forEach(i => { i.checked = false; });
      this.el.querySelectorAll('.calculator-option--selected').forEach(o => o.classList.remove('calculator-option--selected'));
      this.el.querySelectorAll('[data-action="next"][data-step]').forEach(b => { b.disabled = true; });

      // Clear checkboxes (sidebar + drawer)
      this.el.querySelectorAll('input[data-filter]').forEach(i => { i.checked = false; });
      document.querySelectorAll('input[data-drawer-filter]').forEach(i => { i.checked = false; });

      if (this.el.hasAttribute('data-browse-mode')) {
        // In browse mode just clear filters and re-render
        this._applyFilters();
        this._renderTable();
        this._renderActiveFilters();
        this._renderPagination();
        this._updateFilterCount();
      } else {
        // Normal mode — go back to start screen
        this.el.querySelector('[data-table]')?.classList.add('hidden');
        this._showStep(0);
        const restartWrap = this.el.querySelector('[data-restart-wrap]');
        if (restartWrap) restartWrap.classList.add('hidden');
      }
    }

    // ── Table ─────────────────────────────────────────────────────────────────

    _showTable(fromQuiz) {
      // Hide all quiz steps
      this.el.querySelectorAll('[data-step]').forEach(s => s.classList.add('hidden'));

      // Show table
      const table = this.el.querySelector('[data-table]');
      table?.classList.remove('hidden');

      // Sync sidebar checkboxes to filterState
      this._syncCheckboxesToState();

      this._applyFilters();
      this._renderTable();
      this._renderActiveFilters();
      this._renderPagination();
    }

    _bindTable() {
      // Sidebar checkboxes
      this.el.addEventListener('change', e => {
        const input = e.target.closest('input[data-filter]');
        if (!input || input.dataset.drawerFilter) return;
        this._onFilterChange(input);
      });

      // Sort headers
      this.el.addEventListener('click', e => {
        const sortBtn = e.target.closest('[data-sort]');
        if (!sortBtn) return;
        const key = sortBtn.dataset.sort;
        this.sort.dir = (this.sort.key === key && this.sort.dir === 'asc') ? 'desc' : 'asc';
        this.sort.key = key;
        this._applyFilters();
        this._renderTable();
        this._renderPagination();
        this._updateSortIcons();
      });

      // Pagination
      this.el.addEventListener('click', e => {
        const pageBtn = e.target.closest('[data-page]');
        if (!pageBtn) return;
        this.page = parseInt(pageBtn.dataset.page);
        this._renderTable();
        this._renderPagination();
        this.el.querySelector('[data-table]')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });

      // Remove active filter pill
      this.el.addEventListener('click', e => {
        const removeBtn = e.target.closest('[data-remove-filter]');
        if (!removeBtn) return;
        const { group, value } = removeBtn.dataset;
        if (this.filterState[group]) {
          this.filterState[group] = this.filterState[group].filter(v => v !== value);
          if (!this.filterState[group].length) delete this.filterState[group];
        }
        this._syncCheckboxesToState();
        this._applyFilters();
        this._renderTable();
        this._renderActiveFilters();
        this._renderPagination();
        this._updateFilterCount();
      });

      // Clear all
      this.el.addEventListener('click', e => {
        if (!e.target.closest('[data-clear-all]')) return;
        this.filterState = {};
        this._syncCheckboxesToState();
        document.querySelectorAll('input[data-drawer-filter]').forEach(i => { i.checked = false; });
        this._applyFilters();
        this._renderTable();
        this._renderActiveFilters();
        this._renderPagination();
        this._updateFilterCount();
      });

      // Per-page selector — handles both the top-toolbar and bottom-footer copies
      // and keeps them in sync.
      this.el.addEventListener('change', e => {
        const select = e.target.closest('[data-per-page-select]');
        if (!select) return;
        const n = parseInt(select.value, 10);
        if (!n || n === this.itemsPerPage) return;
        this.itemsPerPage = n;
        this.page = 1;
        // Sync the other selectors
        this.el.querySelectorAll('[data-per-page-select]').forEach(other => {
          if (other !== select) other.value = String(n);
        });
        this._renderTable();
        this._renderPagination();
      });
    }

    _onFilterChange(input) {
      const group = input.dataset.filter;
      const value = input.value;
      if (!this.filterState[group]) this.filterState[group] = [];

      if (input.checked) {
        if (!this.filterState[group].includes(value)) this.filterState[group].push(value);
      } else {
        this.filterState[group] = this.filterState[group].filter(v => v !== value);
        if (!this.filterState[group].length) delete this.filterState[group];
      }

      // Keep drawer in sync — use find() instead of an attribute selector so values containing "
      // (e.g. 6" Concrete) don't break querySelector.
      const drawerId = this.tool === 'wall' ? 'CalcFilterDrawer' : 'CalcFloorFilterDrawer';
      const drawer = document.getElementById(drawerId);
      if (drawer) {
        const mirror = Array.from(drawer.querySelectorAll('input[data-filter]'))
          .find(el => el.dataset.filter === group && el.value === value);
        if (mirror) mirror.checked = input.checked;
      }

      this.page = 1;
      this._applyFilters();
      this._renderTable();
      this._renderActiveFilters();
      this._renderPagination();
      this._updateFilterCount();
    }

    _syncCheckboxesToState() {
      // Sidebar + drawer — drawer is outside this.el so use document
      const drawerId = this.tool === 'wall' ? 'CalcFilterDrawer' : 'CalcFloorFilterDrawer';
      const drawer = document.getElementById(drawerId);
      const inputs = [
        ...this.el.querySelectorAll('input[data-filter]'),
        ...(drawer ? drawer.querySelectorAll('input[data-filter]') : [])
      ];
      inputs.forEach(input => {
        const group = input.dataset.filter;
        input.checked = !!(this.filterState[group]?.includes(input.value));
      });
    }

    _applyFilters() {
      this.filtered = this.data.filter(item => {
        for (const [group, values] of Object.entries(this.filterState)) {
          if (!values.length) continue;
          const itemVal = item[group];
          if (Array.isArray(itemVal)) {
            // list field — check if any selected value appears in item's list
            if (!values.some(v => itemVal.includes(v))) return false;
          } else {
            if (!values.includes(itemVal)) return false;
          }
        }
        return true;
      });

      if (this.sort.key) {
        const dir = this.sort.dir === 'asc' ? 1 : -1;
        this.filtered.sort((a, b) => (a[this.sort.key] - b[this.sort.key]) * dir);
      }

      this.page = 1;
      this._updateResultsCount();
    }

    _renderTable() {
      const tbody = this.el.querySelector('[data-results-body]');
      if (!tbody) return;

      const start = (this.page - 1) * this.itemsPerPage;
      const pageItems = this.filtered.slice(start, start + this.itemsPerPage);

      const baseCols = this.tool === 'floor-ceiling' ? 6 : 4;
      const colspan = baseCols + (this.detailsModal ? 1 : 0);

      if (!pageItems.length) {
        tbody.innerHTML = `<tr><td colspan="${colspan}" class="text-center text-subtext" style="padding:3rem 0;">No assemblies match your filters. <button class="btn--link" data-clear-all type="button">Clear all filters</button></td></tr>`;
        return;
      }

      tbody.innerHTML = pageItems.map((item, i) => {
        const absoluteIndex = start + i;
        const details = Array.isArray(item.structure_details)
          ? item.structure_details.map(d => `<li>${this._escape(d)}</li>`).join('')
          : `<li>${this._escape(item.structure_details)}</li>`;

        const img = item.image_url
          ? `<img src="${this._escape(item.image_url)}" alt="${this._escape(item.name)}" class="js-calc-zoomable" width="120" loading="lazy">`
          : '';

        const extraCols = this.tool === 'floor-ceiling'
          ? `<td class="calculator-col--iic text-center">${item.iic}</td>
             <td class="calculator-col--hiic text-center">${item.hiic}</td>`
          : '';

        // Chevron column — visible only on mobile via CSS. Opens the details modal.
        // Rendered whenever a details modal exists for this calculator (any tool).
        const chevronCol = this.detailsModal
          ? `<td class="calculator-col--chevron">
              <button class="calculator-row-toggle btn--inherit" type="button" data-show-details data-row-index="${absoluteIndex}" aria-label="View assembly details">
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="9 18 15 12 9 6"></polyline></svg>
              </button>
            </td>`
          : '';

        return `<tr>
          <td class="calculator-col--image">${img}</td>
          <td class="calculator-col--name font-body-bolder">${this._escape(item.name)}</td>
          <td class="calculator-col--details"><ul class="calculator-details-list">${details}</ul></td>
          <td class="calculator-col--stc text-center">${item.stc}</td>
          ${extraCols}
          ${chevronCol}
        </tr>`;
      }).join('');
    }

    _renderActiveFilters() {
      const container = this.el.querySelector('[data-active-filters]');
      if (!container) return;

      const pills = [];
      let totalActive = 0;

      for (const [group, values] of Object.entries(this.filterState)) {
        for (const value of values) {
          totalActive++;
          pills.push(`
            <button class="facet-remove btn btn--secondary flex relative" data-remove-filter data-group="${this._escape(group)}" data-value="${this._escape(value)}" type="button" aria-label="Remove ${this._escape(value)} filter">
              <span class="btn__text flex items-center justify-between gap-2">
                <span class="facet-title text-wrap">${this._escape(value)}</span>
                <svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" class="icon icon-close icon--extra-small icon--thick" fill="none" viewBox="0 0 24 24">
                  <path d="M18.75 5.13496L5.25 18.8544" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M18.75 18.8544L5.25 5.13496" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </span>
            </button>
          `);
        }
      }

      if (totalActive > 1) {
        pills.push(`<button class="facet-clear-all btn--link" data-clear-all type="button">Clear all</button>`);
      }

      container.innerHTML = pills.join('');
      this._updateFilterCount(totalActive);
    }

    _renderPagination() {
      const container = this.el.querySelector('[data-pagination]');
      if (!container) return;

      const total = this.filtered.length;
      if (!total) { container.innerHTML = ''; return; }

      const totalPages = Math.ceil(total / this.itemsPerPage);
      const startIdx = (this.page - 1) * this.itemsPerPage + 1;
      const endIdx = Math.min(this.page * this.itemsPerPage, total);

      // Theme convention: .pagination__item is a 4rem circular pill, .pagination__item--previous/--next add the caret.
      // Range is plain text — no .pagination__item class (which would force it into a circle).
      const caretLeft = `<svg class="icon icon-caret-left rtl-flip-x icon--extra-small icon--thick" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12.5 16.25L6.25 10L12.5 3.75" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
      const caretRight = `<svg class="icon icon-caret-right rtl-flip-x icon--extra-small icon--thick" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M7.5 3.75L13.75 10L7.5 16.25" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>`;

      let html = '';
      html += `<span class="calculator-pagination__range text-subtext"><span class="font-body-bolder">${startIdx}-${endIdx}</span> of ${total}</span>`;

      if (totalPages > 1) {
        const prevDisabled = this.page === 1;
        const nextDisabled = this.page === totalPages;
        html += `<button class="pagination__item pagination__item--previous" data-page="${this.page - 1}" type="button" aria-label="Previous page"${prevDisabled ? ' disabled aria-disabled="true"' : ''}>${caretLeft}</button>`;
        html += `<button class="pagination__item pagination__item--next" data-page="${this.page + 1}" type="button" aria-label="Next page"${nextDisabled ? ' disabled aria-disabled="true"' : ''}>${caretRight}</button>`;
      }

      container.innerHTML = html;
    }

    _updateResultsCount() {
      const el = this.el.querySelector('[data-results-count]');
      if (el) el.textContent = `${this.filtered.length} result${this.filtered.length !== 1 ? 's' : ''}`;
    }

    _updateSortIcons() {
      this.el.querySelectorAll('[data-sort]').forEach(btn => {
        const icon = btn.querySelector('.calculator-sort-icon');
        if (!icon) return;
        if (btn.dataset.sort === this.sort.key) {
          icon.textContent = this.sort.dir === 'asc' ? '↑' : '↓';
        } else {
          icon.textContent = '↕';
        }
      });
    }

    _updateFilterCount(count) {
      const countEl = this.el.querySelector('[data-filter-count]');
      if (!countEl) return;
      const n = count ?? Object.values(this.filterState).flat().length;
      countEl.textContent = `(${n})`;
      countEl.classList.toggle('hidden', n === 0);
    }

    // ── Mobile drawer ─────────────────────────────────────────────────────────

    _bindDrawer() {
      // Trigger button
      this.el.addEventListener('click', e => {
        if (!e.target.closest('[data-filter-drawer-trigger]')) return;
        const drawerId = this.tool === 'wall' ? 'CalcFilterDrawer' : 'CalcFloorFilterDrawer';
        const drawer = document.getElementById(drawerId);
        if (drawer) {
          drawer.removeAttribute('hidden');
          setTimeout(() => drawer.setAttribute('active', ''), 10);
        }
      });

      // Drawer filter changes — sync to sidebar state.
      // Uses find() instead of an attribute selector so values containing " (e.g. 6" Concrete)
      // don't throw on the querySelector with the interpolated value.
      document.addEventListener('change', e => {
        const input = e.target.closest('input[data-drawer-filter]');
        if (!input) return;
        const group = input.dataset.filter;
        const value = input.value;
        const sidebar = Array.from(this.el.querySelectorAll('input[data-filter]'))
          .find(el => el.dataset.filter === group && el.value === value);
        if (sidebar) {
          sidebar.checked = input.checked;
          this._onFilterChange(sidebar);
        } else {
          this._onFilterChange(input);
        }
      });
    }

    // ── Lightbox + details modal ─────────────────────────────────────────────
    // Both reuse the theme's <basic-modal> pattern. Markup lives in the section
    // liquid; these methods locate the elements, swap content, and call .show().
    // basic-modal moves itself to document.body on first show, so the lightbox
    // click handler listens on document (not this.el) to catch images inside the
    // details modal too.

    _showModal(modal, trigger) {
      if (!modal) return;
      if (typeof modal.show === 'function') {
        modal.show(trigger);
      } else {
        // Fallback if the custom element isn't upgraded yet.
        modal.removeAttribute('hidden');
        modal.setAttribute('open', '');
        setTimeout(() => modal.setAttribute('active', ''), 10);
      }
    }

    _bindLightbox() {
      const modalId = this.el.dataset.lightboxId;
      this.lightboxModal = modalId ? document.getElementById(modalId) : null;
      this.lightboxImg = this.lightboxModal?.querySelector('.assembly-calculator__lightbox-img');
      if (!this.lightboxModal || !this.lightboxImg) return;

      document.addEventListener('click', e => {
        const img = e.target.closest('.js-calc-zoomable');
        if (!img) return;
        e.preventDefault();
        this.lightboxImg.src = img.currentSrc || img.src;
        this.lightboxImg.alt = img.alt || '';
        this._showModal(this.lightboxModal, img);
      });
    }

    _bindDetailsModal() {
      const modalId = this.el.dataset.detailsId;
      this.detailsModal = modalId ? document.getElementById(modalId) : null;
      if (!this.detailsModal) return;

      this.el.addEventListener('click', e => {
        // Direct chevron click works on any breakpoint.
        let trigger = e.target.closest('[data-show-details]');

        // On mobile, clicking anywhere on the card (except the image, which has its
        // own lightbox handler) also opens the details modal.
        if (!trigger) {
          const row = e.target.closest('tr');
          const isMobile = window.matchMedia('(max-width: 767px)').matches;
          if (row && isMobile && !e.target.closest('.calculator-col--image, .js-calc-zoomable')) {
            trigger = row.querySelector('[data-show-details]');
          }
        }
        if (!trigger) return;

        const idx = parseInt(trigger.dataset.rowIndex, 10);
        const item = this.filtered[idx];
        if (!item) return;

        const imgEl = this.detailsModal.querySelector('[data-detail-img]');
        if (imgEl) {
          imgEl.src = item.image_url || '';
          imgEl.alt = item.name || '';
        }
        const nameEl = this.detailsModal.querySelector('[data-detail-name]');
        if (nameEl) nameEl.textContent = item.name || '';

        const listEl = this.detailsModal.querySelector('[data-detail-list]');
        if (listEl) {
          const details = Array.isArray(item.structure_details) ? item.structure_details : [item.structure_details];
          listEl.innerHTML = details.filter(Boolean).map(d => `<li>${this._escape(d)}</li>`).join('');
        }

        const setVal = (sel, val) => {
          const el = this.detailsModal.querySelector(sel);
          if (el) el.textContent = val ?? '';
        };
        setVal('[data-detail-stc]', item.stc);
        setVal('[data-detail-iic]', item.iic);
        setVal('[data-detail-hiic]', item.hiic);

        this._showModal(this.detailsModal, trigger);
      });
    }

    // ── Helpers ───────────────────────────────────────────────────────────────

    _escape(str) {
      return String(str ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
    }
  }

  // Init all calculators on the page
  document.querySelectorAll('.assembly-calculator').forEach(el => new AssemblyCalculator(el));
})();